import { useRef } from 'react'
import { Form, Button, Popconfirm } from 'antd'
import Checkbox from 'app/components/Checkbox/index'
import { default as CustomPopconfirm } from 'app/components/Popconfirm/index'
import { CloseCircleFilled } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import EMTAble from 'app/components/EMTable'
import { CannedService } from 'app/api/Models/CannedServiceModel'
import TableSummary from './TableSummary'
import { ReactSVG } from 'react-svg'
import TrashCan from 'app/assets/icons/FA6/trash-can.svg'
import Editable from 'app/components/Editable'
import { useDataSource } from 'app/screens/cannedService/hooks/useDataSource'
import { useCanneryServicesContext } from 'app/providers/CannedServicesProvider'
import { useSubtotal } from 'app/screens/cannedService/hooks/useSubtotal'
import { useCurrency } from 'app/hooks/useCurrency'
import {
	toDollars,
	toCents,
	focusConfirmButton
} from 'app/screens/cannedService/helper'
import useNextTabbableElement from 'app/hooks/useNextTabbableElement'

import styles from 'app/screens/cannedService/serviceTables/styles.module.scss'

const SubletTable = () => {
	const { t } = useTranslation()
	const { isFixedPrice } = useCanneryServicesContext()
	const { subtotalValidator, invalidFieldKey, setInvalidFieldKey } =
		useSubtotal()
	const { getCurrencySymbol } = useCurrency()
	const [form] = Form.useForm()
	const {
		dataSourceWithKeys,
		onOrderChanged,
		setField,
		handleSave,
		handleDelete
	} = useDataSource(CannedService.SUBLETS, form)
	const { captureNextTabbableElement, focusNextTabbableElemement } =
		useNextTabbableElement()

	const trashButtonRef = useRef(null)
	const feeCentsRef = useRef(null)

	const columns = [
		{
			key: 'sort',
			className: styles.dragAndDropColumn
		},
		{
			className: styles.titleColumn,
			title: t('canned_service.sublets.title'),
			dataIndex: 'name',
			render: (val, record) => (
				<Editable
					className={styles.titleCell}
					type="text"
					dataIndex="name"
					value={val}
					placeholder={t('canned_service.sublets.empty_title')}
					handleSave={() => handleSave(record, 'name')}
					onClick={() => setField(record, 'name')}
					rules={[
						{
							required: true,
							message: t('canned_service.error.min_one_character')
						}
					]}
				/>
			)
		},
		{ className: styles.normalColumn },
		{ className: styles.normalColumn },
		{
			className: styles.normalColumn,
			title: t('canned_service.sublets.price'),
			dataIndex: 'price_cents',
			render: (val, record) => (
				<CustomPopconfirm
					open={invalidFieldKey === `${record.key}-price_cents`}
					overlayClassName={styles.fixedPricePopup}
					showCancel={false}
					title={t('canned_service.error.fixed_price_exceed')}
					okText={t('canned_service.ok_got_it')}
					onOpenChange={open => {
						if (open) {
							focusConfirmButton()
						}
					}}
					onConfirm={() => {
						setInvalidFieldKey('')
						setField(record, 'price_cents')
						feeCentsRef.current!.focus()
					}}>
					<Editable
						customInputRef={feeCentsRef}
						className={classNames(
							styles.normalCell,
							invalidFieldKey === `${record.key}-price_cents` && styles.hasError
						)}
						prefix={getCurrencySymbol()}
						suffix={
							invalidFieldKey === `${record.key}-price_cents` && (
								<CloseCircleFilled />
							)
						}
						type="currency"
						dataIndex="price_cents"
						value={toDollars(val)}
						handleSave={value => {
							if (invalidFieldKey) return Promise.reject()
							return handleSave(record, 'price_cents', value)
						}}
						onClick={() => setField(record, 'price_cents')}
						rules={[
							...(isFixedPrice
								? [
										{
											validator: (_, value) => {
												return subtotalValidator({
													name: 'price_cents',
													key: record.key,
													otherFieldValue: 1,
													newValue: toCents(value),
													currentValue: record.price_cents
												})
											}
										}
									]
								: [])
						]}
					/>
				</CustomPopconfirm>
			)
		},
		{
			className: styles.normalColumn,
			title: t('canned_service.tax'),
			dataIndex: 'taxable',
			render: (_, record) => (
				<Form.Item
					name={`taxable-${record.key}`}
					valuePropName="checked"
					initialValue={!record.taxable}>
					<Checkbox
						className={styles.taxCell}
						onChange={() => handleSave(record, 'taxable')}
						data-testid={`tax-checkbox-${record.key}`}>
						{t('canned_service.taxable')}
					</Checkbox>
				</Form.Item>
			)
		},
		{
			title: '',
			className: styles.actionColumn,
			render: (_, record) => (
				<Popconfirm
					destroyTooltipOnHide={true}
					title={t('canned_service.delete_row_confirm')}
					okText={t('canned_service.yes')}
					cancelText={t('canned_service.no')}
					onConfirm={() => {
						handleDelete(record.key)
						focusNextTabbableElemement()
					}}
					onOpenChange={open => {
						if (open) {
							captureNextTabbableElement()
							focusConfirmButton()
						} else trashButtonRef.current!.focus()
					}}>
					<Button type="ghost" ref={trashButtonRef}>
						<ReactSVG
							src={TrashCan}
							className={styles.deleteIcon}
							data-testid={`delete-icon-${record.key}`}
						/>
					</Button>
				</Popconfirm>
			)
		}
	]

	return (
		<Form form={form} component={false}>
			<EMTAble
				size="small"
				className={styles.serviceTable}
				draggable={true}
				dataSource={dataSourceWithKeys}
				columns={columns}
				onOrderChanged={onOrderChanged}
				rowClassName={() => 'editableRow'}
				scroll={{ x: 'max-content' }}
				summary={() => <TableSummary />}
			/>
		</Form>
	)
}

export default SubletTable
